import videojs, { VideoJsPlayer } from 'video.js';

import PlayerError, { ErrorMessage } from '@src/errors/PlayerError';
import { Event, EventName } from '@src/types/event';
import { UserPlayerOptions } from '@src/types/player';
import { attachPluginToPlayer } from '@src/utils/plugin';

const Plugin = videojs.getPlugin('plugin');

export const PLUGIN_NAME = 'eventListenerPlugin';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Options {}

const eventNameToCallbackNameMap = new Map<
  EventName,
  keyof Pick<
    UserPlayerOptions,
    | 'onPlay'
    | 'onPlaying'
    | 'onLoadStart'
    | 'onCanPlay'
    | 'onPause'
    | 'onEnded'
    | 'onVolumeChange'
    | 'onPlayerResize'
    | 'onResize'
    | 'onFullscreenChange'
  >
>();

eventNameToCallbackNameMap.set(Event.PLAY, 'onPlay');
eventNameToCallbackNameMap.set(Event.PLAYING, 'onPlaying');
eventNameToCallbackNameMap.set(Event.LOADSTART, 'onLoadStart');
eventNameToCallbackNameMap.set(Event.CANPLAY, 'onCanPlay');
eventNameToCallbackNameMap.set(Event.PAUSE, 'onPause');
eventNameToCallbackNameMap.set(Event.ENDED, 'onEnded');
eventNameToCallbackNameMap.set(Event.VOLUMECHANGE, 'onVolumeChange');
eventNameToCallbackNameMap.set(Event.PLAYERRESIZE, 'onPlayerResize');
eventNameToCallbackNameMap.set(Event.RESIZE, 'onResize');
eventNameToCallbackNameMap.set(Event.FULLSCREENCHANGE, 'onFullscreenChange');

class EventListenerPlugin extends Plugin {
  constructor(player: VideoJsPlayer, options?: Options) {
    super(player, options);

    attachPluginToPlayer(this, player);

    this.setupListeners();
  }

  protected setupListeners() {
    this.player.options_.onInit?.(this.player);

    this.player.on(Event.ERROR, this.handleError);

    [...eventNameToCallbackNameMap.entries()].forEach(
      ([eventName, listenerKey]) => {
        this.player.on(eventName, (evt: videojs.EventTarget.Event) => {
          this.player.options_?.[listenerKey]?.(evt);
        });
      }
    );
  }

  protected handleError = (evt: videojs.EventTarget.Event) => {
    const error = new PlayerError(ErrorMessage.VIDEO_JS_ERROR, {
      cause: this.player.error(),
    });
    this.player.options_.onError?.(error, evt);
  };

  protected disposeListeners() {
    [...eventNameToCallbackNameMap.entries()].forEach(
      ([eventName, listenerKey]) => {
        this.player.off(eventName, (evt: videojs.EventTarget.Event) => {
          this.player.options_?.[listenerKey]?.(evt);
        });
      }
    );

    this.player.off(Event.ERROR, this.handleError);
  }

  dispose() {
    this.disposeListeners();
    super.dispose();
  }
}

videojs.registerPlugin(PLUGIN_NAME, EventListenerPlugin);

export type EventListenerPluginFactoryFn = (
  options?: Options
) => EventListenerPlugin;

export default EventListenerPlugin;
